import { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  GFE_SERVICE_LABELS,
  gfeDisplayDate,
  goodFaithEstimateService,
  type GfeDelivery,
  type GfeRequest,
  type GfeService,
} from "../services/goodFaithEstimateService";

const SERVICES: GfeService[] = ["Membership", "OptionalVideoVisit", "RequiredVideoVisit", "Other"];

/**
 * CONS-5: ask for a written Good Faith Estimate. The patient notice tells uninsured and self-pay
 * patients they can ask here (MTC:Consent:GfeRequestUrl), so the page is reachable during enrollment
 * too — RootLayout lets this one path through — which is exactly when someone wants an estimate.
 */
export default function GoodFaithEstimatePage({ onBack }: { onBack?: () => void }) {
  const [requests, setRequests] = useState<GfeRequest[] | null>(null);
  const [services, setServices] = useState<GfeService[]>([]);
  const [details, setDetails] = useState("");
  const [delivery, setDelivery] = useState<GfeDelivery | "">("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<GfeRequest | null>(null);

  useEffect(() => {
    goodFaithEstimateService
      .mine()
      .then(setRequests)
      .catch(() => setRequests([]));
  }, []);

  const toggle = (s: GfeService) =>
    setServices((current) => (current.includes(s) ? current.filter((x) => x !== s) : [...current, s]));

  const submit = async () => {
    setError(null);
    if (services.length === 0) return setError("Choose at least one service to estimate.");
    if (services.includes("Other") && !details.trim()) return setError("Tell us what else you would like an estimate for.");
    if (!delivery) return setError("Choose how you would like to receive the estimate.");
    setBusy(true);
    try {
      const created = await goodFaithEstimateService.request({ services, details: details.trim() || undefined, delivery });
      setSent(created);
      setRequests((current) => [created, ...(current ?? [])]);
      setServices([]);
      setDetails("");
      setDelivery("");
    } catch (e) {
      setError(
        axios.isAxiosError(e) && (e.response?.data as { message?: string })?.message
          ? (e.response!.data as { message: string }).message
          : "We couldn't send your request. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, sm: 5 } }}>
      {onBack && (
        <Button onClick={onBack} sx={{ mb: 1, ml: -1, textTransform: "none" }}>
          ← Back
        </Button>
      )}
      <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
        Request a Good Faith Estimate
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        If you are uninsured or not using insurance, you can ask for a written estimate of expected charges. We'll
        provide it within three business days.{" "}
        <Link href="https://www.cms.gov/medical-bill-rights/know-your-rights/no-insurance" target="_blank" rel="noopener">
          About your rights
        </Link>
      </Typography>

      {sent && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Request received. Your estimate will be provided by {gfeDisplayDate(sent.dueBy)}.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <FormGroup>
            <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: "text.primary" }}>
              What would you like an estimate for?
            </FormLabel>
            {SERVICES.map((s) => (
              <FormControlLabel
                key={s}
                control={<Checkbox checked={services.includes(s)} onChange={() => toggle(s)} />}
                label={GFE_SERVICE_LABELS[s]}
              />
            ))}
          </FormGroup>

          <TextField
            label={services.includes("Other") ? "What else? (required)" : "Anything else we should know (optional)"}
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
            multiline
            minRows={2}
            helperText="Describe the service, not your health — for example, how many months of membership."
          />

          <Box>
            <FormLabel id="gfe-delivery" sx={{ fontWeight: 600, color: "text.primary" }}>
              How should we send it?
            </FormLabel>
            <RadioGroup
              aria-labelledby="gfe-delivery"
              value={delivery}
              onChange={(e) => setDelivery(e.target.value as GfeDelivery)}
            >
              <FormControlLabel value="Email" control={<Radio />} label="By email" />
              <FormControlLabel value="Mail" control={<Radio />} label="On paper, to my address on file" />
            </RadioGroup>
          </Box>

          {error && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
          <Button variant="contained" size="large" onClick={submit} disabled={busy} sx={{ borderRadius: 999, textTransform: "none", fontWeight: 700 }}>
            {busy ? "Sending…" : "Request estimate"}
          </Button>
        </Stack>
      </Paper>

      {requests && requests.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
            Your requests
          </Typography>
          <Stack spacing={1.5}>
            {requests.map((r) => (
              <Paper key={r.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap", mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 600 }}>{r.services.map((s) => GFE_SERVICE_LABELS[s]).join(", ")}</Typography>
                  <Chip
                    size="small"
                    color={r.answeredAt ? "success" : "default"}
                    label={r.answeredAt ? "Provided" : `Due by ${gfeDisplayDate(r.dueBy)}`}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Requested {new Date(r.requestedAt).toLocaleDateString()}
                  {r.answeredAt && ` · provided ${new Date(r.answeredAt).toLocaleDateString()}${r.answerNote ? ` (${r.answerNote})` : ""}`}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Container>
  );
}
